import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import JobMonitor from '../components/JobMonitor';
import { InstanzUebersicht, Job } from '../types';

const RolloutPage: React.FC = () => {
  const [kunden, setKunden] = useState<InstanzUebersicht[]>([]);
  const [tag, setTag] = useState('latest');
  const [ausgewaehlt, setAusgewaehlt] = useState<Record<string, boolean>>({});
  const [job, setJob] = useState<Job | null>(null);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    api
      .get<InstanzUebersicht[]>('/api/instanzen')
      .then(res => setKunden(res.data.filter(i => i.typ === 'KUNDE' && i.registriert)))
      .catch(err => setFehler(err.response?.data?.detail || 'Instanzen nicht ladbar.'));
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollJob = useCallback((id: number) => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const res = await api.get<Job>(`/api/jobs/${id}`);
        setJob(res.data);
        if (res.data.status !== 'LAEUFT') {
          stopPolling();
          setLaeuft(false);
        }
      } catch {
        stopPolling();
        setLaeuft(false);
      }
    }, 3000);
  }, []);

  useEffect(() => () => stopPolling(), []);

  const starten = async () => {
    const slugs = Object.keys(ausgewaehlt).filter(s => ausgewaehlt[s]);
    if (slugs.length === 0) {
      setFehler('Bitte mindestens eine Instanz wählen.');
      return;
    }
    if (!window.confirm(
        `Rollout von Tag '${tag}' auf ${slugs.length} Instanz(en) starten? Jede bekommt vorher ein Backup.`)) {
      return;
    }
    setFehler('');
    setLaeuft(true);
    try {
      const res = await api.post<Job>('/api/rollout', { image_tag: tag, instanz_slugs: slugs });
      setJob(res.data);
      if (res.data.status === 'LAEUFT') {
        pollJob(res.data.id);
      } else {
        // WARTET_FREIGABE: nicht pollen, damit der freigabe_link im State erhalten bleibt.
        setLaeuft(false);
      }
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Rollout konnte nicht gestartet werden.');
      setLaeuft(false);
    }
  };

  const fortsetzen = async () => {
    if (!job) return;
    setFehler('');
    setLaeuft(true);
    try {
      const res = await api.post<Job>(`/api/jobs/${job.id}/fortsetzen`);
      setJob(res.data);
      pollJob(res.data.id);
    } catch (err: any) {
      setFehler(err.response?.data?.detail || 'Fortsetzen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="k-seite">
      <div className="k-seite__kopf">
        <div>
          <span className="k-eyebrow">{'// fleet / rollout'}</span>
          <h1 className="k-h1">Rollout</h1>
        </div>
      </div>

      {fehler && <div className="k-fehler">{fehler}</div>}

      <section className="k-block">
        <h2 className="k-h2">Ziel-Image-Tag</h2>
        <div className="k-feld">
          <input value={tag} onChange={e => setTag(e.target.value)} disabled={laeuft} />
        </div>

        <h2 className="k-h2">Kundeninstanzen</h2>
        {kunden.length === 0 ? (
          <div className="k-hinweis">Keine Kundeninstanzen registriert.</div>
        ) : (
          <div className="k-tabelle__rahmen">
            <table className="k-tabelle">
              <thead>
                <tr>
                  <th></th>
                  <th>Slug</th>
                  <th>Firma</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {kunden.map(i => (
                  <tr key={i.slug}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!ausgewaehlt[i.slug]}
                        disabled={laeuft}
                        onChange={e =>
                          setAusgewaehlt(a => ({ ...a, [i.slug]: e.target.checked }))
                        }
                      />
                    </td>
                    <td className="k-mono">{i.slug}</td>
                    <td>{i.firmenname}</td>
                    <td className="k-mono">{i.image_tag || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="k-seite__aktionen k-seite__aktionen--links">
          <button className="k-btn" onClick={starten} disabled={laeuft}>
            {laeuft ? 'Rollout läuft …' : 'Rollout starten'}
          </button>
        </div>
      </section>

      {job && <JobMonitor job={job} onFortsetzen={fortsetzen} laeuft={laeuft} />}
    </div>
  );
};

export default RolloutPage;
